import React, { useState, useEffect } from 'react';
import { T, font, mono } from '../utils/theme';
import * as api from '../utils/api';
import { Card, Sect, Btn, Inp, SearchBar, Toggle, StatusBadge, SBadge, Modal, IcPlus, IcCheck, IcEdit, IcTrash, IcMsg, Ic, useIsMobile, matchesSearch } from './UI';
import { money } from '../utils/money';
import DeleteProductModal from './DeleteProductModal';
import DisableProductModal from './DisableProductModal';

const IcHistory = <Ic d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />;
const IcNote = <Ic d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />;

export default function AdminProducts({ lang, products, suppliers, conversations, reload, Lk }) {
  const isMobile = useIsMobile();
  const [search, setSearch] = useState('');
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProd, setNewProd] = useState({ name: '', sku: '', costPrice: '', qtyOrdered: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [historyId, setHistoryId] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [activityData, setActivityData] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [openComment, setOpenComment] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [stockFilter, setStockFilter] = useState('all'); // 'all', 'out', 'in', 'disabled'
  const [disableConfirm, setDisableConfirm] = useState(null);
  const [msgModal, setMsgModal] = useState(null); // { id, mode: 'note' | 'toggle' }
  const [msgText, setMsgText] = useState('');

  const [pageSize, setPageSize] = useState(50);
  const [hoveredId, setHoveredId] = useState(null);

  // A version a supplier only suggested is not part of the catalogue until we receive stock of it, so
  // it is not in this list and not in these counts. That is what keeps ten suggestions from turning
  // into ten product rows: the eight we never order are only ever visible inside their quoting item.
  const catalogue = products.filter(p => p.variant_state !== 'provisional');
  const suggestedCount = products.length - catalogue.length;
  // A disabled (discontinued) product is neither in nor out of stock: it has its own tab, and in "All" it
  // sits at the bottom, greyed, so it can still be found and enabled again.
  const isOff = (p) => p.status === 'discontinued';
  const disabledCount = catalogue.filter(isOff).length;
  const outCount = catalogue.filter(p => !isOff(p) && !p.in_stock).length;
  const inCount = catalogue.length - outCount - disabledCount;

  const filtered = React.useMemo(() => {
    return products
      .filter(p => {
        if (p.variant_state === 'provisional') return false;
        if (stockFilter === 'out' && (p.in_stock || isOff(p))) return false;
        if (stockFilter === 'in' && (!p.in_stock || isOff(p))) return false;
        if (stockFilter === 'disabled' && !isOff(p)) return false;
        if (search && !matchesSearch(search, p.name) && !matchesSearch(search, p.sku || '')) return false;
        return true;
      })
      .sort((a, b) => (isOff(a) - isOff(b)) || a.name.localeCompare(b.name));
  }, [products, stockFilter, search]);

  const visible = filtered.slice(0, pageSize);

  useEffect(() => {
    if (historyId) {
      Promise.all([
        api.getOrderHistory(historyId).catch(() => []),
        api.getProductActivity(historyId).catch(() => []),
      ]).then(([orders, activity]) => {
        setHistoryData(orders);
        setActivityData(activity);
      });
    }
  }, [historyId]);

  const [highlightId, setHighlightId] = useState(null);

  const addProduct = async () => {
    if (!newProd.name.trim()) return;
    const addedName = newProd.name.trim();
    setLoading(true);
    const result = await api.createProduct({ name: addedName, sku: newProd.sku || null, cost_price: newProd.costPrice ? parseFloat(newProd.costPrice) : null, qty_ordered: newProd.qtyOrdered ? parseInt(newProd.qtyOrdered) : 0 });
    setNewProd({ name: '', sku: '', costPrice: '', qtyOrdered: '' });
    setShowAddProduct(false);
    setStockFilter('all');
    await reload();
    setLoading(false);
    // Auto-search for the new product so it appears instantly
    if (result?.id) {
      setSearch(addedName);
      setHighlightId(result.id);
      setTimeout(() => setHighlightId(null), 3000);
    }
  };

  const saveEdit = async (id) => {
    setLoading(true);
    await api.updateProduct(id, { name: editForm.name, sku: editForm.sku || null, cost_price: editForm.costPrice !== '' ? parseFloat(editForm.costPrice) : null, selling_price: editForm.sellingPrice !== '' ? parseFloat(editForm.sellingPrice) : null, qty_ordered: editForm.qtyOrdered ? parseInt(editForm.qtyOrdered) : 0 });
    setEditingId(null);
    await reload();
    setLoading(false);
  };

  const doToggleStock = async (id) => {
    const product = products.find(p => p.id === id);
    if (product && product.in_stock && product.admin_note) {
      setMsgText(product.admin_note);
      setMsgModal({ id, mode: 'toggle' });
    } else {
      await api.toggleStock(id);
      await reload();
    }
  };

  const doEnable = async (id) => {
    setLoading(true);
    try { await api.setDiscontinued(id, false); await reload(); } finally { setLoading(false); }
  };

  // Next to the in-stock toggle: Disable for a live product, Enable for a disabled one. Disabling asks
  // first, enabling does not - it only puts the product back as in stock.
  const disableControl = (p) => isOff(p) ? (
    <Btn variant="ghostSm" onClick={() => doEnable(p.id)} title={lang === 'zh' ? '重新启用此产品' : 'Enable this product again'}
      style={{ fontSize: 11, fontWeight: 700, padding: isMobile ? '0 16px' : '4px 8px', minHeight: isMobile ? 44 : undefined, color: T.success, border: `1px solid ${T.success}60` }}>
      {lang === 'zh' ? '启用' : 'Enable'}
    </Btn>
  ) : (
    <Btn variant="ghostSm" onClick={() => setDisableConfirm(p.id)} title={lang === 'zh' ? '停用（已停产）' : 'Disable (discontinued)'}
      style={{ fontSize: 11, fontWeight: 700, padding: isMobile ? '0 16px' : '4px 8px', minHeight: isMobile ? 44 : undefined }}>
      {lang === 'zh' ? '停用' : 'Disable'}
    </Btn>
  );

  const stockControl = (p) => isOff(p)
    ? <div style={{ width: 40, fontSize: 10, fontWeight: 800, color: T.dim, textTransform: 'uppercase' }}>{lang === 'zh' ? '停用' : 'Off'}</div>
    : <Toggle on={p.in_stock} onToggle={() => doToggleStock(p.id)} />;

  const saveNote = async (id) => {
    await api.updateProduct(id, { admin_note: msgText.trim() || null });
    setMsgModal(null);
    await reload();
  };

  const doToggleWithMessage = async (id) => {
    setMsgModal(null);
    await api.toggleStock(id);
    if (msgText.trim()) {
      await api.updateProduct(id, { admin_note: msgText.trim() });
    }
    await reload();
  };

  const handleExport = async () => {
    const csv = await api.exportProductsCSV();
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'products_export.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const parseRows = (rows) => {
    const items = [];
    for (const obj of rows) {
      const name = obj['Product Name'] || obj['product name'] || obj['Name'] || obj['product_name'];
      if (!name || !String(name).trim()) continue;
      let cost = obj['Cost'] || obj['cost'] || obj['Cost Price'] || obj['cost_price'] || null;
      if (cost && typeof cost === 'string') cost = parseFloat(cost.replace(/[¥$,\s]/g, '')) || null;
      if (cost && typeof cost === 'number') cost = cost;
      const qty = parseInt(obj['Quantity'] || obj['quantity'] || obj['Qty'] || obj['qty'] || 0) || 0;
      const orderedRaw = obj['Ordered status'] || obj['In Stock'] || obj['in_stock'] || 'true';
      let inStock = true;
      if (orderedRaw === 'false' || orderedRaw === 'FALSE' || orderedRaw === '0' || orderedRaw === false) inStock = false;
      const sku = obj['SKU'] || obj['sku'] || null;
      const existingByName = products.find(p => p.name.toLowerCase() === String(name).trim().toLowerCase());
      const existingBySku = sku ? products.find(p => p.sku && p.sku.toLowerCase() === String(sku).toLowerCase()) : null;
      const duplicate = existingByName || existingBySku;
      items.push({ name: String(name).trim(), sku: sku ? String(sku) : null, cost_price: typeof cost === 'number' ? cost : null, qty_ordered: qty, in_stock: inStock, duplicate: duplicate || null });
    }
    return items;
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'xlsx' || ext === 'xls') {
      // Excel file
      const XLSX = require('xlsx');
      const reader = new FileReader();
      reader.onload = (ev) => {
        const data = new Uint8Array(ev.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet);
        const items = parseRows(rows);
        if (items.length > 0) {
          const decisions = {};
          items.forEach((item, i) => { decisions[i] = item.duplicate ? 'skip' : 'import'; });
          setImportPreview({ items, decisions });
        }
      };
      reader.readAsArrayBuffer(file);
    } else {
      // CSV / TSV file
      const reader = new FileReader();
      reader.onload = (ev) => {
        const text = ev.target.result;
        const lines = text.split('\n').filter(l => l.trim());
        if (lines.length < 2) return;
        const firstLine = lines[0];
        const sep = firstLine.includes(';') ? ';' : firstLine.includes('\t') ? '\t' : ',';
        const headers = firstLine.split(sep).map(h => h.replace(/"/g, '').trim());
        const rows = [];
        for (let i = 1; i < lines.length; i++) {
          const vals = lines[i].split(sep).map(v => v.replace(/"/g, '').trim());
          const obj = {};
          headers.forEach((h, idx) => { obj[h] = vals[idx] || null; });
          rows.push(obj);
        }
        const items = parseRows(rows);
        if (items.length > 0) {
          const decisions = {};
          items.forEach((item, i) => { decisions[i] = item.duplicate ? 'skip' : 'import'; });
          setImportPreview({ items, decisions });
        }
      };
      reader.readAsText(file, 'UTF-8');
    }
    e.target.value = '';
  };

  const doImport = async () => {
    if (!importPreview) return;
    setLoading(true);
    const toImport = [];
    const toUpdate = [];
    importPreview.items.forEach((item, i) => {
      const decision = importPreview.decisions[i];
      if (decision === 'import') {
        toImport.push({ name: item.name, sku: item.sku, cost_price: item.cost_price, qty_ordered: item.qty_ordered, in_stock: item.in_stock });
      } else if (decision === 'update' && item.duplicate) {
        toUpdate.push({ id: item.duplicate.id, cost_price: item.cost_price, qty_ordered: item.qty_ordered });
      }
    });
    if (toImport.length > 0) await api.importProducts(toImport);
    for (const u of toUpdate) {
      await api.updateProduct(u.id, { cost_price: u.cost_price, qty_ordered: u.qty_ordered });
    }
    setImportPreview(null);
    await reload();
    setLoading(false);
  };

  const addPinnedComment = async (productId) => {
    if (!commentText.trim()) return;
    await api.updateProduct(productId, { pinned_comment: commentText.trim() });
    setCommentText('');
    await reload();
  };

  const removePinnedComment = async (productId) => {
    await api.updateProduct(productId, { pinned_comment: null });
    await api.deleteProductConversations(productId);
    await reload();
  };

  const sendReply = async (productId) => {
    if (!commentText.trim()) return;
    await api.addConversation({ product_id: productId, author: 'Admin', author_type: 'admin', text: commentText.trim() });
    setCommentText('');
    await reload();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: T.text }}>{Lk('products')} <span style={{ fontSize: 14, color: T.dim, fontWeight: 600 }}>({catalogue.length})</span></h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="secondary" style={{ fontSize: 12, padding: '7px 12px' }} onClick={handleExport}>↓ {Lk('exportProducts')}</Btn>
          <label style={{ display: 'inline-flex', cursor: 'pointer' }}>
            <span style={{ fontFamily: font, fontSize: 12, fontWeight: 700, padding: '7px 12px', background: T.surfaceAlt, color: T.text, border: `1px solid ${T.border}`, borderRadius: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>↑ {Lk('importProducts')}</span>
            <input type="file" accept=".csv,.xlsx,.xls" onChange={handleImport} style={{ display: 'none' }} />
          </label>
        </div>
      </div>

      {/* Sticky: Add Product + Search + Filters */}
      <div style={{ position: 'sticky', top: 0, zIndex: 20, background: T.bg, paddingBottom: 8, paddingTop: 4 }}>
        {/* Add Product - Collapsible */}
        <div style={{ marginBottom: showAddProduct ? 10 : 6 }}>
          <div onClick={() => setShowAddProduct(!showAddProduct)} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 8px 6px 0', userSelect: 'none' }}>
            <span style={{ fontSize: 12, color: T.dim }}>{showAddProduct ? '▼' : '▶'}</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: showAddProduct ? T.accent : T.muted, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{IcPlus} {Lk('addProduct')}</span>
          </div>
          {showAddProduct && (
            <Card style={{ marginTop: 6 }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr auto', gap: 10, alignItems: 'end' }}>
                <Inp label={Lk('productName')} placeholder="e.g. Steel Beam H200" value={newProd.name} onChange={e => setNewProd({ ...newProd, name: e.target.value })} onKeyDown={e => { if (e.key === 'Enter') addProduct(); }} />
                <Inp label={`${Lk('sku')} (opt)`} placeholder={Lk('autoGen')} value={newProd.sku} onChange={e => setNewProd({ ...newProd, sku: e.target.value })} />
                <Inp label={Lk('costPrice')} placeholder="0.00" type="number" step="0.01" value={newProd.costPrice} onChange={e => setNewProd({ ...newProd, costPrice: e.target.value })} />
                <Inp label={Lk('qtyOrdered')} placeholder="0" type="number" value={newProd.qtyOrdered} onChange={e => setNewProd({ ...newProd, qtyOrdered: e.target.value })} />
                <Btn onClick={addProduct} disabled={loading}>{IcPlus} {Lk('add')}</Btn>
              </div>
            </Card>
          )}
        </div>

        {/* Search */}
        <SearchBar value={search} onChange={setSearch} placeholder={Lk('searchProduct')} style={{ marginBottom: 8 }} />

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: lang === 'zh' ? '全部' : 'All', count: products.length },
            { key: 'out', label: lang === 'zh' ? '缺货' : 'Out of Stock', count: outCount },
            { key: 'in', label: lang === 'zh' ? '有货' : 'In Stock', count: inCount },
            ...(disabledCount > 0 || stockFilter === 'disabled' ? [{ key: 'disabled', label: lang === 'zh' ? '已停用' : 'Disabled', count: disabledCount }] : []),
          ].map(f => (
            <div key={f.key} onClick={() => { setStockFilter(f.key); setPageSize(50); }} style={{
              padding: '6px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer', userSelect: 'none',
              background: stockFilter === f.key ? T.accent : T.surfaceAlt,
              color: stockFilter === f.key ? '#fff' : T.muted,
              border: `1px solid ${stockFilter === f.key ? T.accent : T.border}`,
              transition: 'all 0.15s',
            }}>
              {f.label} ({f.count})
            </div>
          ))}
        </div>
      </div>

      {suggestedCount > 0 && (
        <Card style={{ marginBottom: 10, borderColor: T.warn + '60', background: T.warnBg }}>
          <div style={{ fontSize: 13, color: T.text }}>
            {lang === 'zh'
              ? `${suggestedCount} 个供应商建议的版本尚未进入产品列表 — 在「报价中」内审核。`
              : `${suggestedCount} supplier-suggested version${suggestedCount > 1 ? 's' : ''} not in this list yet — review ${suggestedCount > 1 ? 'them' : 'it'} inside the item on the Quoting screen.`}
          </div>
        </Card>
      )}
      {/* Column headers - hidden on mobile */}
      {!isMobile && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 90px 70px 70px 55px 125px 80px 100px 210px', padding: '0 18px 8px', fontSize: 10, fontWeight: 800, color: T.dim, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          <div>{Lk('productName')}</div><div>{Lk('sku')}</div><div>{Lk('cost')}</div><div>{lang === 'zh' ? '售价' : 'Sell'}</div><div>{Lk('qty')}</div><div>{Lk('inStock')}</div><div>{Lk('status')}</div><div>{Lk('dateChanged')}</div><div>{Lk('actions')}</div>
        </div>
      )}

      {/* Product rows */}
      {visible.map(p => {
        const isHighlighted = highlightId === p.id;
        if (editingId === p.id) {
          return (
            <Card key={p.id} style={{ marginBottom: 4, padding: '10px 18px', borderColor: T.accent + '50' }}>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 100px 70px 70px 55px auto', gap: 8, alignItems: 'center' }}>
                <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} placeholder={Lk('productName')} style={{ fontFamily: font, fontSize: 13, fontWeight: 700, padding: '7px 10px', background: T.bg, border: `1px solid ${T.accent}50`, borderRadius: 6, color: T.text, outline: 'none' }} />
                <input value={editForm.sku} onChange={e => setEditForm({ ...editForm, sku: e.target.value })} placeholder="SKU" style={{ fontFamily: mono, fontSize: 11, padding: '7px 8px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.muted, outline: 'none' }} />
                {isMobile ? (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                    <input type="number" step="0.01" value={editForm.costPrice} onChange={e => setEditForm({ ...editForm, costPrice: e.target.value })} placeholder="Cost" style={{ fontFamily: mono, fontSize: 12, padding: '7px 8px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none' }} />
                    <input type="number" step="0.01" value={editForm.sellingPrice} onChange={e => setEditForm({ ...editForm, sellingPrice: e.target.value })} placeholder="Sell" style={{ fontFamily: mono, fontSize: 12, padding: '7px 8px', background: T.bg, border: `1px solid ${T.success}50`, borderRadius: 6, color: T.success, outline: 'none' }} />
                    <input type="number" value={editForm.qtyOrdered} onChange={e => setEditForm({ ...editForm, qtyOrdered: e.target.value })} placeholder="Qty" style={{ fontFamily: mono, fontSize: 12, padding: '7px 8px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none' }} />
                  </div>
                ) : (
                  <>
                    <input type="number" step="0.01" value={editForm.costPrice} onChange={e => setEditForm({ ...editForm, costPrice: e.target.value })} placeholder="Cost" style={{ fontFamily: mono, fontSize: 12, padding: '7px 8px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none', width: '100%' }} />
                    <input type="number" step="0.01" value={editForm.sellingPrice} onChange={e => setEditForm({ ...editForm, sellingPrice: e.target.value })} placeholder="Sell" style={{ fontFamily: mono, fontSize: 12, padding: '7px 8px', background: T.bg, border: `1px solid ${T.success}50`, borderRadius: 6, color: T.success, outline: 'none', width: '100%' }} />
                    <input type="number" value={editForm.qtyOrdered} onChange={e => setEditForm({ ...editForm, qtyOrdered: e.target.value })} style={{ fontFamily: mono, fontSize: 12, padding: '7px 8px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none', width: '100%' }} />
                  </>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  <Btn variant="small" onClick={() => saveEdit(p.id)}>{IcCheck} {Lk('save')}</Btn>
                  <Btn variant="ghost" onClick={() => setEditingId(null)}>{Lk('cancel')}</Btn>
                </div>
              </div>
            </Card>
          );
        }

        const hasComments = p.pinned_comment || conversations.filter(c => c.product_id === p.id).length > 0;

        return (
          <Card key={p.id} id={`product-${p.id}`} hoverBg={!isHighlighted} style={{ marginBottom: 4, padding: isMobile ? '10px 14px' : '12px 18px', transition: 'all 0.3s', ...(isOff(p) ? { background: T.surfaceAlt } : {}), ...(isHighlighted ? { borderColor: T.accent, boxShadow: `0 0 12px ${T.accent}40`, background: T.accent + '08' } : {}) }}>
            {isMobile ? (
              /* Mobile card layout */
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 6 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: isOff(p) ? T.dim : T.text, fontSize: 13, textDecoration: isOff(p) ? 'line-through' : 'none' }}>{p.name}</div>
                    <div style={{ fontFamily: mono, fontSize: 10, color: T.dim, marginTop: 2 }}>{p.sku || '—'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: mono, fontSize: 11, color: T.muted }}>{p.cost_price != null ? `Cost: ${money(p.cost_price)}` : ''}</div>
                    <div style={{ fontFamily: mono, fontSize: 11, color: p.selling_price ? T.success : T.dim }}>{p.selling_price != null ? `Sell: ${money(p.selling_price)}` : ''}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 6, borderTop: `1px solid ${T.border}` }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <StatusBadge status={p.status} lang={lang} />
                    {!isOff(p) && stockControl(p)}
                    {disableControl(p)}
                  </div>
                  <div style={{ display: 'flex', gap: 3 }}>
                    <Btn variant="ghostSm" style={p.admin_note ? { background: T.warn + '30', color: T.warn, border: `1px solid ${T.warn}60` } : {}} onClick={() => { setMsgText(p.admin_note || ''); setMsgModal({ id: p.id, mode: 'note' }); }} title={lang === 'zh' ? '缺货留言' : 'Out of stock message'}>{IcNote}</Btn>
                    <Btn variant="ghostSm" onClick={() => setHistoryId(p.id)}>{IcHistory}</Btn>
                    <Btn variant={hasComments ? 'orange' : 'ghostSm'} onClick={() => setOpenComment(openComment === p.id ? null : p.id)}>{IcMsg}</Btn>
                    <Btn variant="ghostSm" onClick={() => { setEditingId(p.id); setEditForm({ name: p.name, sku: p.sku, costPrice: p.cost_price ?? '', sellingPrice: p.selling_price ?? '', qtyOrdered: p.qty_ordered ?? 0 }); }}>{IcEdit}</Btn>
                    <Btn variant="dangerSm" title={Lk('delete')} onClick={() => setDeleteConfirm(p.id)}>{IcTrash}</Btn>
                  </div>
                </div>
              </div>
            ) : (
              /* Desktop grid layout */
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 90px 70px 70px 55px 125px 80px 100px 210px', alignItems: 'center' }}>
                <div style={{ fontWeight: 700, color: isOff(p) ? T.dim : T.text, fontSize: 13, textDecoration: isOff(p) ? 'line-through' : 'none' }}>{p.name}</div>
                <div style={{ fontFamily: mono, fontSize: 11, color: T.muted }}>{p.sku || '—'}</div>
                <div style={{ fontFamily: mono, fontSize: 12, color: T.muted }}>{p.cost_price != null ? `${money(p.cost_price)}` : '—'}</div>
                <div style={{ fontFamily: mono, fontSize: 12, color: p.selling_price ? T.success : T.dim }}>{p.selling_price != null ? `${money(p.selling_price)}` : '—'}</div>
                <div style={{ fontFamily: mono, fontSize: 12, color: T.muted }}>{p.qty_ordered || '—'}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {stockControl(p)}
                  {disableControl(p)}
                </div>
                <StatusBadge status={p.status} lang={lang} />
                <div style={{ fontSize: 11, color: T.dim }}>{p.untick_date || '—'}</div>
                <div style={{ display: 'flex', gap: 3 }}>
                  <Btn variant="ghostSm" style={p.admin_note ? { background: T.warn + '30', color: T.warn, border: `1px solid ${T.warn}60` } : {}} onClick={() => { setMsgText(p.admin_note || ''); setMsgModal({ id: p.id, mode: 'note' }); }} title={lang === 'zh' ? '缺货留言' : 'Out of stock message'}>{IcNote}</Btn>
                  <Btn variant="ghostSm" onClick={() => setHistoryId(p.id)} title={lang === 'zh' ? '历史' : 'History'}>{IcHistory}</Btn>
                  <Btn variant={hasComments ? 'orange' : 'ghostSm'} onClick={() => setOpenComment(openComment === p.id ? null : p.id)}>{IcMsg}</Btn>
                  <Btn variant="ghostSm" onClick={() => { setEditingId(p.id); setEditForm({ name: p.name, sku: p.sku, costPrice: p.cost_price ?? '', sellingPrice: p.selling_price ?? '', qtyOrdered: p.qty_ordered ?? 0 }); }}>{IcEdit}</Btn>
                  <Btn variant="dangerSm" title={Lk('delete')} onClick={() => setDeleteConfirm(p.id)}>{IcTrash}</Btn>
                </div>
              </div>
            )}

            {/* Comment panel */}
            {openComment === p.id && (
              <div style={{ marginTop: 10, padding: '12px', background: T.bg, borderRadius: 8, border: `1px solid ${T.border}` }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: T.dim, textTransform: 'uppercase', marginBottom: 8 }}>{Lk('comments')}</div>
                {p.pinned_comment ? (
                  <div style={{ padding: '8px 10px', background: T.orangeBg, border: `1px solid ${T.orange}30`, borderRadius: 6, marginBottom: 8 }}>
                    <div style={{ fontSize: 13, color: T.text, fontWeight: 600 }}>{p.pinned_comment}</div>
                    <div style={{ fontSize: 10, color: T.dim, marginTop: 3 }}>Admin note — visible to all suppliers</div>
                    <Btn variant="dangerSm" style={{ marginTop: 6 }} onClick={() => removePinnedComment(p.id)}>{IcTrash} Remove</Btn>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                    <input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Add note visible to all suppliers..."
                      onKeyDown={e => { if (e.key === 'Enter') addPinnedComment(p.id); }}
                      style={{ flex: 1, fontFamily: font, fontSize: 12, padding: '7px 10px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none' }} />
                    <Btn variant="small" onClick={() => addPinnedComment(p.id)}>Post</Btn>
                  </div>
                )}
                {/* Conversation threads */}
                {conversations.filter(c => c.product_id === p.id).map(c => (
                  <div key={c.id} style={{ fontSize: 12, padding: '3px 0', borderBottom: `1px solid ${T.border}20` }}>
                    <span style={{ fontWeight: 700, color: c.author_type === 'admin' ? T.accent : T.success }}>{c.author}: </span>
                    <span style={{ color: T.text }}>{c.text}</span>
                    <span style={{ color: T.dim, fontSize: 10, marginLeft: 6 }}>{c.created_at?.split('T')[0]}</span>
                  </div>
                ))}
                {p.pinned_comment && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                    <input value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Reply..."
                      onKeyDown={e => { if (e.key === 'Enter') sendReply(p.id); }}
                      style={{ flex: 1, fontFamily: font, fontSize: 12, padding: '7px 10px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none' }} />
                    <Btn variant="small" style={{ padding: '5px 10px', fontSize: 11 }} onClick={() => sendReply(p.id)}>Send</Btn>
                  </div>
                )}
              </div>
            )}
          </Card>
        );
      })}

      {filtered.length === 0 && search && (
        <div style={{ color: T.dim, fontSize: 13, fontStyle: 'italic', padding: 20 }}>{Lk('noResults')}</div>
      )}

      {/* Show More */}
      {filtered.length > pageSize && (
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <Btn variant="secondary" onClick={() => setPageSize(ps => ps + 50)} style={{ fontSize: 12 }}>
            {lang === 'zh' ? `显示更多 (${filtered.length - pageSize} 剩余)` : `Show More (${filtered.length - pageSize} remaining)`}
          </Btn>
        </div>
      )}
      {filtered.length > 0 && (
        <div style={{ textAlign: 'center', fontSize: 11, color: T.dim, padding: '4px 0' }}>
          {lang === 'zh' ? `显示 ${Math.min(pageSize, filtered.length)} / ${filtered.length}` : `Showing ${Math.min(pageSize, filtered.length)} of ${filtered.length}`}
        </div>
      )}

      <DisableProductModal lang={lang} products={products} productId={disableConfirm} Lk={Lk}
        onClose={() => setDisableConfirm(null)}
        onDisabled={async () => { setDisableConfirm(null); await reload(); }} />

      {/* Out of stock note / toggle modal */}
      <Modal open={msgModal !== null} onClose={() => setMsgModal(null)}>
        {(() => {
          if (!msgModal) return null;
          const mp = products.find(p => p.id === msgModal.id);
          if (!mp) return null;
          const isToggle = msgModal.mode === 'toggle';
          return (
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 6 }}>
                {isToggle ? (lang === 'zh' ? '标记为缺货' : 'Mark as Out of Stock') : (lang === 'zh' ? '缺货留言' : 'Out of Stock Message')}
              </div>
              <div style={{ fontSize: 13, color: T.muted, marginBottom: 16 }}>
                <span style={{ fontWeight: 700, color: T.text }}>{mp.name}</span>
              </div>
              <div style={{ fontSize: 12, color: T.dim, marginBottom: 8 }}>
                {lang === 'zh' ? '供应商可见的留言（留空则无留言）' : 'Message visible to all suppliers — leave blank for none.'}
              </div>
              <textarea
                value={msgText}
                onChange={e => setMsgText(e.target.value)}
                placeholder={lang === 'zh' ? '例如：请检查门店库存，或联系我们获取到货时间' : 'e.g. Please check back in store, or contact us for ETA'}
                rows={3}
                style={{ width: '100%', fontFamily: font, fontSize: 13, padding: '8px 10px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, outline: 'none', resize: 'vertical', boxSizing: 'border-box' }}
              />
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <Btn variant="secondary" onClick={() => setMsgModal(null)} style={{ flex: 1 }}>{Lk('cancel')}</Btn>
                {isToggle ? (
                  <Btn onClick={() => doToggleWithMessage(mp.id)} style={{ flex: 1 }}>
                    {lang === 'zh' ? '保存并标记缺货' : 'Save & Mark out of stock'}
                  </Btn>
                ) : (
                  <Btn onClick={() => saveNote(mp.id)} style={{ flex: 1 }}>
                    {lang === 'zh' ? '保存' : 'Save'}
                  </Btn>
                )}
              </div>
            </div>
          );
        })()}
      </Modal>

      <DeleteProductModal lang={lang} products={products} productId={deleteConfirm} Lk={Lk}
        onClose={() => setDeleteConfirm(null)}
        onDeleted={async () => { setDeleteConfirm(null); await reload(); }} />

      {/* History modal */}
      <Modal open={historyId !== null} onClose={() => setHistoryId(null)}>
        {(() => {
          const hp = products.find(p => p.id === historyId);
          if (!hp) return null;

          const MILESTONE_TYPES = ['out_of_stock', 'newAssigned', 'confirmed', 'shipped', 'received'];
          const eventLabel = {
            out_of_stock: lang === 'zh' ? '标记缺货' : 'Marked out of stock',
            newAssigned: lang === 'zh' ? '已分配' : 'Assigned',
            confirmed: lang === 'zh' ? '订购中' : 'Ordering',
            shipped: lang === 'zh' ? '已发货' : 'Shipped',
            received: lang === 'zh' ? '已收货' : 'Received',
          };
          const eventColor = {
            out_of_stock: T.danger,
            newAssigned: T.accent,
            confirmed: T.purple || '#8B8BE0',
            shipped: T.warn,
            received: T.success,
          };

          // Build timeline: activity_log milestones + order_history completions
          // Deduplicate by (type, date, supplier_id) — guards against double-submit rows
          const seenActivity = new Set();
          const activityEvents = (activityData || [])
            .filter(a => MILESTONE_TYPES.includes(a.type))
            .filter(a => {
              const key = `${a.type}|${a.date}|${a.supplier_id || ''}`;
              if (seenActivity.has(key)) return false;
              seenActivity.add(key);
              return true;
            })
            .map(a => {
              const sup = a.supplier_id ? suppliers.find(s => s.id === a.supplier_id) : null;
              if (a.type === 'confirmed') {
                // Find a matching order_history entry (backfill uses confirmed_date + ~10 days)
                const confirmedMs = new Date(a.date).getTime();
                const ohMatch = (historyData || []).find(oh => {
                  if (sup?.name && oh.supplier_name && oh.supplier_name !== sup.name) return false;
                  const diff = (new Date(oh.date).getTime() - confirmedMs) / 86400000;
                  return diff >= 0 && diff <= 20;
                });
                const qty = ohMatch?.qty ?? (hp.qty_ordered > 0 ? hp.qty_ordered : null);
                const price = ohMatch?.price ?? hp.quotes?.[a.supplier_id]?.price ?? null;
                const parts = [sup?.name, price ? `${money(Number(price))}` : null, qty ? `× ${qty}` : null].filter(Boolean);
                return { date: a.date, time: a.time || '', type: a.type, detail: parts.join(' · ') || null };
              }
              return { date: a.date, time: a.time || '', type: a.type, detail: sup?.name || null };
            });

          // order_history entries as Received events (authoritative for price/qty)
          const orderEvents = (historyData || []).map(oh => ({
            date: oh.date, time: '99:99', type: 'order_received',
            detail: [oh.supplier_name, oh.price ? `${money(oh.price)}` : null, oh.qty ? `× ${oh.qty}` : null].filter(Boolean).join(' · '),
          }));

          // Remove activity_log 'received' entries that overlap with order_history on same date
          const orderDates = new Set(orderEvents.map(e => e.date));
          const filteredActivity = activityEvents.filter(a => !(a.type === 'received' && orderDates.has(a.date)));

          // Convert "01:42 PM" → "13:42" so string sort works correctly across AM/PM
          const to24h = t => {
            if (!t) return '00:00';
            const m = t.match(/(\d+):(\d+)\s*(AM|PM)/i);
            if (!m) return t;
            let h = parseInt(m[1]);
            if (m[3].toUpperCase() === 'PM' && h !== 12) h += 12;
            if (m[3].toUpperCase() === 'AM' && h === 12) h = 0;
            return `${String(h).padStart(2, '0')}:${m[2]}`;
          };
          const WORKFLOW_ORDER = { out_of_stock: 0, newAssigned: 1, confirmed: 2, shipped: 3, received: 4, order_received: 5 };
          const timeline = [...filteredActivity, ...orderEvents].sort((a, b) => {
            if (b.date !== a.date) return b.date.localeCompare(a.date);
            const ta = to24h(a.time), tb = to24h(b.time);
            if (tb !== ta) return tb.localeCompare(ta);
            return (WORKFLOW_ORDER[b.type] ?? 0) - (WORKFLOW_ORDER[a.type] ?? 0);
          });

          return (
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 4 }}>
                {lang === 'zh' ? '产品历史' : 'Product History'}
              </div>
              <div style={{ fontSize: 13, color: T.muted, marginBottom: 16 }}>
                {hp.name} <span style={{ fontFamily: mono, fontSize: 11, color: T.dim }}>{hp.sku}</span>
              </div>
              {timeline.length === 0 ? (
                <div style={{ color: T.dim, fontSize: 13, fontStyle: 'italic' }}>{Lk('noData')}</div>
              ) : (
                <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                  {timeline.map((ev, i) => {
                    const isOrder = ev.type === 'order_received';
                    const label = isOrder ? (lang === 'zh' ? '已收货' : 'Received') : eventLabel[ev.type];
                    const color = isOrder ? T.success : (eventColor[ev.type] || T.muted);
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: `1px solid ${T.border}` }}>
                        {/* Date */}
                        <div style={{ fontFamily: mono, fontSize: 11, color: T.dim, minWidth: 80, paddingTop: 2 }}>{ev.date}</div>
                        {/* Dot */}
                        <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0, marginTop: 4 }} />
                        {/* Label + detail */}
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color }}>{label}</div>
                          {ev.detail && <div style={{ fontSize: 11, color: T.muted, marginTop: 2 }}>{ev.detail}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <Btn variant="secondary" onClick={() => setHistoryId(null)} style={{ marginTop: 16, width: '100%', justifyContent: 'center' }}>{lang === 'zh' ? '关闭' : 'Close'}</Btn>
            </div>
          );
        })()}
      </Modal>

      {/* Import Preview Modal */}
      <Modal open={importPreview !== null} onClose={() => setImportPreview(null)}>
        {importPreview && (
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: T.text, marginBottom: 4 }}>{lang === 'zh' ? '导入预览' : 'Import Preview'}</div>
            <div style={{ fontSize: 12, color: T.muted, marginBottom: 16 }}>{importPreview.items.length} {lang === 'zh' ? '个产品' : 'products found'} · {importPreview.items.filter(i => i.duplicate).length} {lang === 'zh' ? '个重复' : 'duplicates'}</div>

            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <Btn variant="ghostSm" onClick={() => {
                const d = { ...importPreview.decisions };
                importPreview.items.forEach((item, i) => { d[i] = 'import'; });
                setImportPreview({ ...importPreview, decisions: d });
              }}>{lang === 'zh' ? '全部导入' : 'Import All'}</Btn>
              <Btn variant="ghostSm" onClick={() => {
                const d = { ...importPreview.decisions };
                importPreview.items.forEach((item, i) => { d[i] = item.duplicate ? 'skip' : 'import'; });
                setImportPreview({ ...importPreview, decisions: d });
              }}>{lang === 'zh' ? '跳过重复' : 'Skip Duplicates'}</Btn>
              <Btn variant="ghostSm" onClick={() => {
                const d = { ...importPreview.decisions };
                importPreview.items.forEach((item, i) => { d[i] = item.duplicate ? 'update' : 'import'; });
                setImportPreview({ ...importPreview, decisions: d });
              }}>{lang === 'zh' ? '更新重复' : 'Update Duplicates'}</Btn>
            </div>

            <div style={{ maxHeight: 400, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: font }}>
                <thead><tr>
                  {[lang === 'zh' ? '产品' : 'Product', lang === 'zh' ? '成本' : 'Cost', lang === 'zh' ? '数量' : 'Qty', lang === 'zh' ? '状态' : 'Status', lang === 'zh' ? '操作' : 'Action'].map((h, i) => (
                    <th key={i} style={{ textAlign: 'left', padding: '6px 10px', background: T.surfaceAlt, borderBottom: `1px solid ${T.border}`, color: T.dim, fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {importPreview.items.map((item, i) => {
                    const decision = importPreview.decisions[i];
                    const isDup = !!item.duplicate;
                    return (
                      <tr key={i} style={{ background: isDup ? (decision === 'skip' ? T.dangerBg : decision === 'update' ? T.warnBg : 'transparent') : 'transparent', opacity: decision === 'skip' ? 0.5 : 1 }}>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}` }}>
                          <div style={{ fontWeight: 600, color: T.text }}>{item.name}</div>
                          {isDup && <div style={{ fontSize: 10, color: T.warn, marginTop: 2 }}>⚠ {lang === 'zh' ? '已存在:' : 'Exists:'} {item.duplicate.name} ({item.duplicate.sku})</div>}
                        </td>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: mono, color: T.muted }}>{item.cost_price ? `${money(item.cost_price)}` : '—'}</td>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}`, fontFamily: mono }}>{item.qty_ordered || '—'}</td>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}` }}>
                          {isDup ? <SBadge label={lang === 'zh' ? '重复' : 'Duplicate'} color={T.warn} bg={T.warnBg} /> : <SBadge label={lang === 'zh' ? '新' : 'New'} color={T.success} bg={T.successBg} />}
                        </td>
                        <td style={{ padding: '6px 10px', borderBottom: `1px solid ${T.border}` }}>
                          <select value={decision} onChange={e => setImportPreview({ ...importPreview, decisions: { ...importPreview.decisions, [i]: e.target.value } })}
                            style={{ fontFamily: font, fontSize: 11, padding: '4px 8px', background: T.bg, border: `1px solid ${T.border}`, borderRadius: 4, color: T.text, cursor: 'pointer' }}>
                            <option value="import">{lang === 'zh' ? '导入为新' : 'Import as new'}</option>
                            {isDup && <option value="update">{lang === 'zh' ? '更新现有' : 'Update existing'}</option>}
                            <option value="skip">{lang === 'zh' ? '跳过' : 'Skip'}</option>
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <Btn variant="secondary" onClick={() => setImportPreview(null)} style={{ flex: 1 }}>{Lk('cancel')}</Btn>
              <Btn onClick={doImport} disabled={loading} style={{ flex: 1 }}>
                {loading ? (lang === 'zh' ? '导入中...' : 'Importing...') : `${lang === 'zh' ? '确认导入' : 'Confirm Import'} (${importPreview.items.filter((_, i) => importPreview.decisions[i] !== 'skip').length})`}
              </Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
